import { isPlatformAdmin, userPermissionNames, type UserWithRoles } from '@/utils/access';

/**
 * Admin pages that need more than a tenant administrator holds (UAT DEF-174). The admin menu and the header
 * search hide them from anyone who can't use them; the API enforces the same rules.
 * `platform`: the page's API is platform-level (super admin only); `perm`: any of these permissions opens it.
 */
const PAGE_ACCESS: { path: string; platform?: true; perm?: string[] }[] = [
  { path: '/admin/users/tenants', platform: true },
  { path: '/admin/learning/streaks', platform: true },
  { path: '/admin/settings/authentication', platform: true },
  { path: '/admin/settings/cache', platform: true },
  { path: '/admin/settings/smtp', platform: true },
  { path: '/admin/settings/task-monitor', platform: true },
  { path: '/admin/settings/web-services', platform: true },
  { path: '/admin/server/sync', platform: true },
  { path: '/admin/server/integrations', platform: true },
  { path: '/admin/platform/widgets', platform: true },
  { path: '/admin/platform/enrolment-methods', platform: true },
  { path: '/admin/server/field-locks', perm: ['profile_field:manage'] },
  { path: '/admin/platform/site-menu', perm: ['system:admin_access'] },
  { path: '/admin/platform/lea', perm: ['system:admin_access'] },
  { path: '/admin/platform/dashboards', perm: ['dashboard:view'] },
  { path: '/admin/platform/leaderboard', perm: ['xp:manage'] },
];

export const canOpenAdminPage = (user: UserWithRoles, path: string): boolean => {
  if (isPlatformAdmin(user)) return true;
  const rule = PAGE_ACCESS.find((r) => path === r.path || path.startsWith(`${r.path}/`));
  if (!rule) return true;
  if (rule.platform) return false;
  const perms = userPermissionNames(user);
  return (rule.perm ?? []).some((p) => perms.has(p));
};

/** Roles that open the whole admin area (the top bar's Admin button). */
export const FULL_ADMIN_ROLE_NAMES = new Set(['superadmin', 'tenantadmin', 'site_admin', 'tenant_admin']);
export const hasFullAdminAccess = (user: { roles?: { name?: string }[] } | null | undefined): boolean =>
  !!user?.roles?.some((r) => FULL_ADMIN_ROLE_NAMES.has((r.name ?? '').toLowerCase()));

/**
 * The admin pages someone who isn't an administrator can still use because they may create things — a Course
 * Creator / program manager, usually on some categories only (category roles given to them, to a cohort they're
 * in or to their department), or a community creator: creating, the course list (only their categories'
 * courses), the course form / designer / builder and the activity "Manage" pages, the program form / builder.
 * The router lets them in (PrivateRoute), the admin menu and the section side menus show them nothing else, and
 * the API enforces the rest.
 */
const COURSE_CREATOR_PAGES = [
  '/admin/learning/courses/manage',
  '/admin/learning/courses/add',
  '/admin/learning/courses/design',
  '/admin/learning/courses/edit',
  '/admin/learning/courses/build',
  '/admin/dashboard/learning/courses/build',
  '/admin/courses',
];
const PROGRAM_CREATOR_PAGES = [
  '/admin/learning/learning-paths/add',
  '/admin/learning/learning-paths/edit',
  '/admin/learning/learning-paths/build',
];
const COMMUNITY_CREATOR_PAGES = ['/admin/social/communities/add'];

export interface CreatorCapabilities { can_create_course?: boolean; can_create_program?: boolean; can_create_community?: boolean }

const under = (pages: string[], path: string) => pages.some((p) => path === p || path.startsWith(`${p}/`));

export const canOpenCreatorPage = (caps: CreatorCapabilities | null | undefined, path: string): boolean => {
  if (!caps) return false;
  if (caps.can_create_course && (path === '/admin/learning/courses' || path === '/admin/learning/courses/' || under(COURSE_CREATOR_PAGES, path))) return true;
  if (caps.can_create_program && under(PROGRAM_CREATOR_PAGES, path)) return true;
  return !!caps.can_create_community && under(COMMUNITY_CREATOR_PAGES, path);
};
